// ============================================================================
// TRAINING GUIDE SIDEBAR - Topic Navigation
// ============================================================================

import { type PMTopic } from '@/lib/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  BookOpen,
  Compass,
  Search,
  GitBranch,
  Users,
  Rocket,
  BarChart3,
  MessageSquare,
  Code,
  RefreshCw,
} from 'lucide-react'

// Map topic IDs to Lucide icons
const TOPIC_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  strategy: Compass,
  discovery: Search,
  processes: GitBranch,
  organization: Users,
  gtm: Rocket,
  metrics: BarChart3,
  stakeholders: MessageSquare,
  technical: Code,
}

// Map topic colors to Tailwind classes
const COLOR_CLASSES: Record<string, { bg: string; text: string; active: string }> = {
  market: {
    bg: 'bg-market/10',
    text: 'text-market',
    active: 'bg-market/10 text-market',
  },
  business: {
    bg: 'bg-business/10',
    text: 'text-business',
    active: 'bg-business/10 text-business',
  },
  solution: {
    bg: 'bg-solution/10',
    text: 'text-solution',
    active: 'bg-solution/10 text-solution',
  },
}

interface TrainingGuideSidebarProps {
  topics: PMTopic[]
  selectedTopicId: string | null
  onSelectTopic: (topicId: string) => void
  totalChunks: number
  onBackfill?: () => void
  isBackfilling?: boolean
}

export function TrainingGuideSidebar({
  topics,
  selectedTopicId,
  onSelectTopic,
  totalChunks,
  onBackfill,
  isBackfilling,
}: TrainingGuideSidebarProps) {
  // Group topics by color (domain)
  const marketTopics = topics.filter(t => t.color === 'market')
  const businessTopics = topics.filter(t => t.color === 'business')
  const solutionTopics = topics.filter(t => t.color === 'solution')

  return (
    <div className="w-56 bg-white border-r border-warm-200 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-warm-200">
        <h2 className="font-semibold text-text flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-market" />
          Training Guide
        </h2>
        <p className="text-xs text-text-muted mt-1">
          {totalChunks} knowledge chunks
        </p>
        {/* Backfill button when no chunks are classified */}
        {totalChunks === 0 && onBackfill && (
          <Button
            onClick={onBackfill}
            disabled={isBackfilling}
            size="sm"
            className="w-full mt-3 bg-market hover:bg-market/90 text-white text-xs"
          >
            {isBackfilling ? (
              <>
                <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                Classifying...
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1.5" />
                Classify Knowledge
              </>
            )}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-4">
        {/* Market Topics */}
        {marketTopics.length > 0 && (
          <TopicGroup
            label="Market"
            color="market"
            topics={marketTopics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={onSelectTopic}
          />
        )}

        {/* Business Topics */}
        {businessTopics.length > 0 && (
          <TopicGroup
            label="Business"
            color="business"
            topics={businessTopics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={onSelectTopic}
          />
        )}

        {/* Solution Topics */}
        {solutionTopics.length > 0 && (
          <TopicGroup
            label="Solution"
            color="solution"
            topics={solutionTopics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={onSelectTopic}
          />
        )}
      </nav>
    </div>
  )
}

interface TopicGroupProps {
  label: string
  color: 'market' | 'business' | 'solution'
  topics: PMTopic[]
  selectedTopicId: string | null
  onSelectTopic: (topicId: string) => void
}

function TopicGroup({
  label,
  color,
  topics,
  selectedTopicId,
  onSelectTopic,
}: TopicGroupProps) {
  const colors = COLOR_CLASSES[color]

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-1 mb-1">
        <span className={`w-2 h-2 rounded-full bg-${color}`}></span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
          {label}
        </span>
      </div>
      <div className="space-y-0.5">
        {topics.map(topic => {
          const Icon = TOPIC_ICONS[topic.id] || BookOpen
          const isActive = selectedTopicId === topic.id
          const hasContent = topic.chunkCount > 0

          return (
            <button
              key={topic.id}
              onClick={() => onSelectTopic(topic.id)}
              disabled={!hasContent}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? `${colors.active} font-medium`
                  : hasContent
                  ? 'text-text-secondary hover:bg-warm-200/50'
                  : 'text-text-muted cursor-not-allowed opacity-50'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left truncate">{topic.name.replace(' & ', ' ')}</span>
              {hasContent && (
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 h-4 ${
                    isActive ? colors.text : 'text-text-muted'
                  }`}
                >
                  {topic.chunkCount}
                </Badge>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default TrainingGuideSidebar
